/**
 * Paging API를 사용하는 Attendance Query Hook
 * @module Query
 */

import { useContext } from 'react';
import { useQuery } from 'react-query';
import { RootStoreContext } from '../../App';
import { fetchPagingUsers } from '../../services/apis/AttendanceApi';

const useAttendancePagingQuery = (
	date,
	page = 1,
	pageSize = 20,
	nameFilter = '',
	classFilter = '',
) => {
	const { authStore } = useContext(RootStoreContext);

	// date 포맷을 'yyyy-MM-dd' 형식으로 변환
	const formatDate = (dateString) => {
		const parts = dateString.split(',');
		if (parts.length >= 3) {
			const year = parts[0];
			const month = parts[1].padStart(2, '0');
			const day = parts[2].padStart(2, '0');
			return `${year}-${month}-${day}`;
		}
		return dateString;
	};

	const formattedDate = formatDate(date);

	const query = useQuery(
		[
			'attendancePaging',
			formattedDate,
			page,
			pageSize,
			nameFilter,
			classFilter,
		],
		() =>
			fetchPagingUsers({
				page,
				pageSize,
				targetDate: formattedDate,
				nameFilter: nameFilter || undefined, // 빈 문자열이면 undefined로
				classFilter: classFilter || undefined, // 빈 문자열이면 undefined로
				token: authStore.auth?.token,
			}),
		{
			enabled: !!authStore.auth?.token,
			keepPreviousData: true, // 페이지 전환 시 이전 데이터 유지
			staleTime: 30000, // 30초 동안 캐시 유지
			cacheTime: 300000, // 5분 동안 캐시 보관
			onError: (error) => {
				console.error('Attendance paging query error:', error);
			},
		},
	);

	// 데이터 정규화 - 기존 형식과 호환되도록 변환
	const normalizedData = query.data?.users?.map((user) => ({
		id: user.id,
		name: user.name,
		birth: user.birth,
		gender: user.gender,
		description: user.description || '',
		nickname: user.nickname ?? user.nickName ?? user.nick_name ?? '',
		kidId: user.kidId || '',
		photoUrl: user.photoUrl || '',
		profile: user.profile,
		livingPlace: user.livingPlace ?? user.living_place ?? '',
		healthInterest: user.healthInterest ?? user.health_interest ?? '',
		favoriteFood: user.favoriteFood ?? user.favorite_food ?? '',
		favoriteMusic: user.favoriteMusic ?? user.favorite_music ?? '',
		favoriteSport: user.favoriteSport ?? user.favorite_sport ?? '',
		hobby: user.hobby ?? '',
		job: user.job ?? '',
		biggestWorry: user.biggestWorry ?? user.biggest_worry ?? '',
		type: 'ACTIVATE',
		collections: query.data?.collection ? [query.data.collection] : [],
		// attendance 데이터 처리
		state: user.attendance?.length > 0 ? user.attendance[0].state : 'ABSENCE',
		attendanceId: user.attendance?.length > 0 ? user.attendance[0].id : null,
		startTime:
			user.attendance?.length > 0 && user.attendance[0].start_time
				? formatTime(user.attendance[0].start_time)
				: [0, 0],
		endTime:
			user.attendance?.length > 0 && user.attendance[0].end_time
				? formatTime(user.attendance[0].end_time)
				: [0, 0],
	}));

	// 시간 포맷 변환 함수
	function formatTime(timeString) {
		if (!timeString || timeString === '2025-09-15T00:00:00') {
			return [0, 0];
		}
		const date = new Date(timeString);
		return [date.getHours(), date.getMinutes()];
	}

	return {
		data: normalizedData || [],
		loading: query.isLoading || query.isFetching, // isFetching도 포함
		error: query.error,
		refetch: query.refetch,
		// 페이징 정보
		pageInfo: {
			page: query.data?.page || 1,
			pageSize: query.data?.pageSize || 20,
			totalCount: query.data?.totalCount || 0,
			totalPages: query.data?.totalPages || 0,
			attendanceCount: query.data?.attendanceCount || 0,
			absenceCount: query.data?.absenceCount || 0,
		},
		collection: query.data?.collection || null,
		isFetching: query.isFetching, // 추가: refetch 시 로딩 상태
	};
};

export default useAttendancePagingQuery;
